import {
  Alert,
  Button,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ModalOverlay,
  Radio,
  RadioGroup,
  TextField,
  ToggleGroup,
  ToggleGroupButton,
  Typography,
} from '@compass/components'
import { contentStyles } from '@compass/Modal'
import clsx from 'clsx'
import { formatDatetime, useDefaultPredefinedRanges } from 'features/commonUI'
import { useCurrentSubscription } from 'features/subscription'
import { getErrorParams } from 'helpers/data'
import { DateRange } from 'helpers/dateRange'
import { getClientTimezone } from 'helpers/locale'
import { compassRegister } from 'helpers/reactHookForm'
import { EMAIL_VALIDATION } from 'helpers/validation'
import { useToast } from 'hooks'
import { useIdentificationEventsExport } from 'hooks/api/identification_events'
import { useCurrentUser } from 'hooks/api/users'
import { FileSpreadsheet } from 'lucide-react'
import { DateTime } from 'luxon'
import { IdEventsExportBody, VisitsFilter } from 'models'
import { ampli, DataExportRequestedProperties } from 'models/ampli'
import { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useFilterSearchParams } from '../../hooks/useFilterSearchParams'
import { IdentificationUrlKeys, ServerApiVersion } from '../../types'
import { AppliedFilters } from '../AppliedFilters/AppliedFilters'
import { useAppliedFilters } from '../AppliedFilters/useAppliedFilters'

export interface ExportIdentificationEventsModalProps {
  isOpen: boolean
  handleClose: () => void
}

export default function ExportIdentificationEventsModal({ isOpen, handleClose }: ExportIdentificationEventsModalProps) {
  const { data: currentUser } = useCurrentUser()
  const { ranges } = useDefaultPredefinedRanges()
  const { currentSubscriptionId } = useCurrentSubscription()
  const defaultLocalTz = currentUser?.timezone ?? getClientTimezone()
  const appliedFilters = useAppliedFilters()

  const defaultValues = useMemo(
    () => ({
      timeZone: defaultLocalTz,
      dateRangeIndex: 0,
      email: currentUser?.email ?? '',
    }),
    [currentUser?.email, defaultLocalTz]
  )

  const {
    control,
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors: formErrors },
  } = useForm({
    defaultValues,
    values: defaultValues,
  })

  const [exportApiVersion, setExportApiVersion] = useState(ServerApiVersion.V4)
  const { mutate: initiateIdentificationEventExport, isPending } = useIdentificationEventsExport(exportApiVersion)
  const { showToast } = useToast()
  const { adaptedFilterValues, dateRange } = useFilterSearchParams()

  const onClose = useCallback(() => {
    reset()
    handleClose()
  }, [handleClose, reset])

  const onSubmit = useCallback<(data: { timeZone: string; email: string }) => void>(
    (data) => {
      const { timeZone } = data

      initiateIdentificationEventExport(
        {
          params: { subscriptionId: currentSubscriptionId },
          data: identificationEventExportBodyAdapter({
            email: data.email,
            filterValues: adaptedFilterValues,
            dateRange,
            timeZone,
          }),
        },
        {
          onSuccess: () => {
            showToast({
              severity: 'success',
              message: (
                <>
                  Success! Be on the lookout for a <strong>download link</strong> sent to <strong>{data.email}</strong>.
                </>
              ),
            })
            const attributeFiltersToTrack = [
              IdentificationUrlKeys.IpAddress,
              IdentificationUrlKeys.Url,
              IdentificationUrlKeys.LinkedId,
              IdentificationUrlKeys.RequestId,
              IdentificationUrlKeys.VisitorId,
              IdentificationUrlKeys.Origin,
              IdentificationUrlKeys.Environment,
            ]

            const attributeFilters = (Object.keys(adaptedFilterValues) as IdentificationUrlKeys[]).filter((f) =>
              attributeFiltersToTrack.includes(f)
            )

            ampli.dataExportRequested({
              exportFormat: 'CSV',
              timeZone: timeZone === 'UTC' ? 'UTC' : 'Local Time',
              dateRange: (ranges[adaptedFilterValues.period]?.label as any) ?? 'Other',
              attributeFilters: attributeFilters as DataExportRequestedProperties['attributeFilters'],
            })
            onClose()
          },
        }
      )
    },
    [
      initiateIdentificationEventExport,
      currentSubscriptionId,
      adaptedFilterValues,
      dateRange,
      showToast,
      ranges,
      onClose,
    ]
  )

  const emailInputError = getErrorParams('email', formErrors)

  return (
    <ModalOverlay
      isDismissable
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent size='lg'>
        <form onSubmit={handleSubmit(onSubmit)} className={contentStyles()}>
          <DialogHeader>
            <DialogTitle>Export events</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Alert variant='info' icon={<FileSpreadsheet />}>
              This export will contain only the data included in your current plan.
            </Alert>
            <div className={clsx(defaultLocalTz?.toLowerCase() === 'utc' && 'hidden')}>
              <Controller
                name='timeZone'
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    label='Time zone'
                    value={field.value}
                    onChange={field.onChange}
                    classes={{ label: 'text-gray-800' }}
                    className='flex flex-col flex-nowrap'
                  >
                    <Radio value={defaultLocalTz}>Local Time ({defaultLocalTz})</Radio>
                    <Radio value='UTC'>UTC</Radio>
                  </RadioGroup>
                )}
              />
            </div>
            <div className='flex flex-col gap-2'>
              <Typography size='base' weight='medium' className='text-gray-800'>
                Date range
              </Typography>
              <Typography as='dd' size='base' weight='medium'>
                {formatDatetime(dateRange.startDate.toJSDate(), 'precise', getValues('timeZone'), 'standardWithYear')}
                {` - ${formatDatetime(dateRange.endDate.toJSDate(), 'precise', getValues('timeZone'), 'standardWithYear')}`}
              </Typography>
            </div>
            {appliedFilters.size > 0 && (
              <div className='flex flex-col gap-2'>
                <Typography size='base' weight='medium' className='text-gray-800'>
                  Applied filters
                </Typography>
                <AppliedFilters isDisabled />
              </div>
            )}
            <div className='flex flex-col gap-2'>
              <Typography
                size='base'
                weight='medium'
                className='text-gray-800'
                aria-label='export API version selection'
              >
                Export as Server API version
              </Typography>
              <ToggleGroup
                selectedKeys={[exportApiVersion]}
                onSelectionChange={(keys: Set<ServerApiVersion>) => {
                  const version = [...keys][0]
                  setExportApiVersion(version)
                  ampli.csvExportApiVersionSwitched({ ApiVersionJson: version })
                }}
                disallowEmptySelection
                className='w-fit'
              >
                <ToggleGroupButton id={ServerApiVersion.V3}>v3</ToggleGroupButton>
                <ToggleGroupButton id={ServerApiVersion.V4}>v4</ToggleGroupButton>
              </ToggleGroup>
            </div>
            <TextField
              label='Send export link to'
              description="We'll send a download link to this email when the export is ready."
              id='email'
              tone='background1'
              defaultValue={defaultValues.email}
              {...compassRegister(register, 'email', EMAIL_VALIDATION)}
              {...(emailInputError.error && { ...emailInputError })}
              isInvalid={!!emailInputError.error}
              errorMessage={emailInputError.helperText}
              validationBehavior='aria'
              classes={{ description: 'text-xs', label: 'text-gray-800' }}
              className='w-full'
            />
          </DialogBody>

          <DialogFooter>
            <Button
              variant='secondary'
              onPress={() => {
                onClose()
              }}
            >
              Cancel
            </Button>
            <Button type='submit' isDisabled={isPending} isLoading={isPending}>
              Start export
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </ModalOverlay>
  )
}

const convertDateWithZone = (date: DateTime, timeZone: string) => {
  const formattedDate = date.setZone(timeZone, { keepLocalTime: true }).toISO({ includeOffset: true })!

  if (formattedDate) {
    return DateTime.fromISO(formattedDate).toUTC().toString()
  }

  return date.toISO() as string
}

type AppliedEnvironmentFilters = { environmentIds: string[] } | object
type AppliedFilters = { filters: AppliedEnvironmentFilters } | object
type IdentificationEventExportBodyAdapterProps = {
  timeZone: string
  email: string
  filterValues: VisitsFilter
  dateRange: Required<DateRange>
}
function identificationEventExportBodyAdapter({
  email,
  filterValues,
  dateRange,
  timeZone,
}: IdentificationEventExportBodyAdapterProps): IdEventsExportBody {
  const filtersThatNeedsAdapting = ['period', 'since', 'before', 'environment']
  const filteredUrlFilters = Object.keys(filterValues).filter((f) => !filtersThatNeedsAdapting.includes(f))
  const urlFilters = filteredUrlFilters.reduce<Record<string, VisitsFilter[keyof VisitsFilter]>>((acc, filterKey) => {
    acc[filterKey] = filterValues[filterKey as keyof VisitsFilter]
    return acc
  }, {})

  const appliedEnvironmentFilters: AppliedEnvironmentFilters = filterValues.environment
    ? { environmentIds: filterValues.environment }
    : {}

  const appliedFilters: AppliedFilters = filterValues.environment
    ? { filters: { ...appliedEnvironmentFilters, ...urlFilters } }
    : filteredUrlFilters.length > 0
      ? { filters: { ...urlFilters } }
      : {}

  return {
    email,
    dateRange: {
      from: convertDateWithZone(dateRange.startDate, timeZone),
      to: convertDateWithZone(dateRange.endDate, timeZone),
    },
    ...appliedFilters,
  }
}
